"use client"
import Image from "next/image"
import { Apple, Play, ShieldCheck, Timer, Bell } from "lucide-react"
import { useTranslations } from "next-intl"
import Heading from "@/components/pages/home/Heading"

type Props = {
  appleUrl?: string
  googleUrl?: string
}

const StoreBadge = ({
  href,
  icon: Icon,
  title,
  subtitle,
  gradient,
}: {
  href: string
  icon: any
  title: string
  subtitle: string
  gradient?: "blue" | "green"
}) => {
  const gradientClasses =
    gradient === "green"
      ? "from-secondary to-primary"
      : "from-primary to-secondary"

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`group inline-flex items-center gap-3 rounded-2xl px-5 py-3 text-white shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-[1.02] bg-gradient-to-r ${gradientClasses}`}
    >
      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/15 backdrop-blur">
        <Icon className="h-5 w-5" />
      </span>
      <span className="flex flex-col text-right">
        <span className="text-xs/4 opacity-90">{subtitle}</span>
        <span className="text-base font-bold">{title}</span>
      </span>
    </a>
  )
}

export default function AppPromo({ appleUrl = "#", googleUrl = "#" }: Props) {
  const t = useTranslations("Home.app-promo")

  return (
    <section
      aria-labelledby="app-promo"
      className="relative overflow-hidden rounded-3xl border border-white/20 bg-gradient-to-br from-sub_primary/90 via-white to-sub_primary/90 p-6 md:p-10"
    >
      {/* Decorative blobs */}
      <div className="pointer-events-none absolute -right-24 -top-24 h-56 w-56 rounded-full bg-primary/10 blur-3xl" />
      <div className="pointer-events-none absolute -left-24 -bottom-24 h-56 w-56 rounded-full bg-secondary/10 blur-3xl" />

  <Heading title={t("title")} subTitle={t("subTitle")} desc={t("desc")} />

  <div className="relative mt-6 grid grid-cols-1 items-center gap-10 md:mt-10 md:grid-cols-2">
        {/* Text + Badges */}
        <div className="order-2 md:order-1 flex flex-col gap-6">
          <ul className="grid grid-cols-1 gap-3 text-sub_text_color md:grid-cols-2">
            <li className="flex items-center gap-3 rounded-2xl bg-white/70 p-3 backdrop-blur">
              <ShieldCheck className="h-5 w-5 text-primary" />
              <span className="text-sm md:text-base">{t("features.one")}</span>
            </li>
            <li className="flex items-center gap-3 rounded-2xl bg-white/70 p-3 backdrop-blur">
              <Timer className="h-5 w-5 text-primary" />
              <span className="text-sm md:text-base">{t("features.two")}</span>
            </li>
            <li className="flex items-center gap-3 rounded-2xl bg-white/70 p-3 backdrop-blur">
              <Bell className="h-5 w-5 text-primary" />
              <span className="text-sm md:text-base">{t("features.three")}</span>
            </li>
          </ul>

          <div className="mt-2 flex flex-wrap items-center justify-start gap-4">
            <StoreBadge
              href={appleUrl}
              icon={Apple}
              title={t("apple.title")}
              subtitle={t("availableOn")}
              gradient="blue"
            />
            <StoreBadge
              href={googleUrl}
              icon={Play}
              title={t("google.title")}
              subtitle={t("getItOn")}
              gradient="green"
            />
          </div>

          <p className="mt-2 text-sm text-sub_text_color/80">{t("note")}</p>
        </div>

        {/* Visual */}
        <div className="order-1 md:order-2">
          <div className="relative mx-auto aspect-[9/16] w-64 md:w-72 lg:w-80">
            <div className="absolute inset-0 rounded-[2.25rem] bg-gradient-to-br from-primary/15 to-secondary/20 p-1 shadow-[0_20px_80px_-20px_rgba(0,91,150,0.35)]">
              <div className="relative h-full w-full overflow-hidden rounded-[2rem] bg-white">
                <Image
                  src="/App.png"
                  alt="Dr & Coach App"
                  fill
                  sizes="(max-width: 768px) 256px, 320px"
                  className="object-cover"
                  priority
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-white/10" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
